import { Injectable, Inject } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { map, finalize } from "rxjs/operators";   // ✅ single import line
import { NgxSpinnerService } from "ngx-spinner";
import { Observable, Subject } from "rxjs";
import { DOCUMENT } from "@angular/common";
import { LocalStorageService } from "./local-storage.service";
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";
@Injectable({
  providedIn: "root",
})
export class HttpRequestsService {
  private hostUrl = environment.APP_URL;
  public isVerifiedUser = new Subject();
  showCustomMsgOnInternalServerError = false;

  constructor(
    public http: HttpClient,
    private localstorage: LocalStorageService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {}

  getHeader(headerOptions, doNotSendAuthorizationParam) {
    const headerParams = {};
    if (this.localstorage.getLocalStore("language")) {
      headerParams["X-L10N-Locale"] =
        this.localstorage.getLocalStore("language");
    } else {
      headerParams["X-L10N-Locale"] = "en";
    }
    if (doNotSendAuthorizationParam !== true) {
      headerParams["authorization"] = this.localstorage.getLocalStore("token");
    }
    if (headerOptions) {
      Object.assign(headerParams, headerOptions);
    }
    const headers = new HttpHeaders(headerParams);
    return { headers };
  }

  private buildFormUrlEncodedBody(body: any): string {
    const params = new URLSearchParams();
    Object.entries(body || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    return params.toString();
  }

  getSimple(
    url: string,
    useDefaultErrorHandler?: boolean
  ) {
    return new Promise((resolve, reject) => {
      this.http
        .get(`${this.hostUrl}${url}`)
        .pipe(
          map((res) => {
            return res;
          })
        )
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => {
            if (useDefaultErrorHandler) {
              resolve(err);
            } else {
              this.handleError(err);
              reject(err);
            }
          }
        );
    });
  }

  postForm(
    url: string,
    body: any,
    useDefaultErrorHandler?: boolean
  ) {
    return new Promise((resolve, reject) => {
      const options = {
        headers: new HttpHeaders({
          "Content-Type": "application/x-www-form-urlencoded",
        }),
      };

      this.showLoading();
      this.http
        .post(`${this.hostUrl}${url}`, this.buildFormUrlEncodedBody(body), options)
        .pipe(
          map((res) => res),
          finalize(() => this.hideLoading())
        )
        .subscribe(
          (res) => resolve(res),
          (err) => {
            if (useDefaultErrorHandler) {
              resolve(err);
            } else {
              this.handleError(err);
              reject(err);
            }
          }
        );
    });
  }

  post(
  url: string,
  body: any,
  doNotSendAuthorizationParam: boolean = false,
  headerOptions: any = {},
  useDefaultErrorHandler?: boolean
) {
  return new Promise((resolve, reject) => {
    const options = this.getHeader(headerOptions, doNotSendAuthorizationParam);
    this.showLoading();
    this.http.post(`${this.hostUrl}${url}`, body, options)
      .pipe(
        map(res => res),
        finalize(() => this.hideLoading())
      )
      .subscribe(
        res => resolve(res),
        err => {
          if (useDefaultErrorHandler) {
            resolve(err);
          } else {
            this.handleError(err);
            reject(err);
          }
        }
      );
  });
}
  
  get(
    url: string,
    doNotSendAuthorizationParam: boolean = false,
    headerOptions: any = {},
    useDefaultErrorHandler?: boolean
  ) {
    return new Promise((resolve, reject) => {
      const modifiedUrl = this.modifyBusinessId(url);
      const options = this.getHeader(
        headerOptions,
        doNotSendAuthorizationParam
      );
      this.http
        .get(`${modifiedUrl}`, options)
        .pipe(
          map((res) => {
            return res;
          })
        )
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => {
            if (useDefaultErrorHandler) {
              resolve(err);
            } else {
              this.handleError(err);
              reject(err);
            }
          }
        );
    });
  }

  put(
    url,
    body: any,
    headerOptions: any = {},
    doNotSendAuthorizationParam: boolean = false
  ) {
    return new Promise((resolve, reject) => {
      const options = this.getHeader(
        headerOptions,
        doNotSendAuthorizationParam
      );
      this.http
        .put(`${this.hostUrl}${url}`, body, options)
        .pipe(
          map((res) => {
            return res;
          })
        )
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => {
            this.handleError(err);
            reject(err);
          }
        );
    });
  }

  delete(
    url,
    headerOptions: any = {},
    doNotSendAuthorizationParam: boolean = false,
    useDefaultErrorHandler?: boolean
  ) {
    return new Promise((resolve, reject) => {
      const options = this.getHeader(
        headerOptions,
        doNotSendAuthorizationParam
      );
      this.http
        .delete(`${this.hostUrl}${url}`, options)
        .pipe(
          map((res) => {
            return res;
          })
        )
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => {
            if (useDefaultErrorHandler) {
              resolve(err);
            } else {
              this.handleError(err);
              reject(err);
            }
          }
        );
    });
  }

  handleError(err) {
    let error;
    switch (err.status) {
      case 400:
        error = err.error?.error || err.error?.message || "Bad request. Please check your input.";
        break;
      case 401:
        error = err.error?.error || "Unauthorized. Please log in again.";
        this.router.navigate(["/auth/login"]);
        this.localstorage.clearStorage();
        break;
      case 404:
        if (typeof err.error === "string") {
          try {
            error = JSON.parse(err.error).error;
          } catch {
            error = "Resource not found.";
          }
          break;
        }
        error = err.error?.error || "Resource not found.";
        break;
      case 412:
        error = err.error?.error || "Email verification required. Please verify your email before proceeding.";
        this.router.navigate(["/auth/email-verification"]);
        break;
      case 500:
        if (this.showCustomMsgOnInternalServerError) {
          error = "There is a problem setting up the bank account detail.";
          this.showCustomMsgOnInternalServerError = false;
        } else {
          console.log("error : ", err);
          if (err?.error?.errorData?.length) {
            error = err.error.errorData.message;
          } else if (err?.error?.error) {
            error = err.error.error;
          } else if (err?.error?.message) {
            error = err.error.message;
          } else {
            error = "Server error. Please try again later.";
          }
        }
        break;
      case 503:
        error = "Service temporarily unavailable. Please try again later.";
        break;
      case 0:
        error =
          "Unable to connect to the server. Please check your internet connection.";
        break;
      default:
        error = err.error?.error || err.error?.message || "An error occurred. Please try again.";
        break;
    }
    this.hideLoading();
    if (error) {
      this.toastr.error(error, "Error");
    }
  }

  showLoading() {
    this.spinner.show();
    this.document.body.classList.add("preventEvents");
  }

  hideLoading() {
    this.spinner.hide();
    this.document.body.classList.remove("preventEvents");
  }

  getIPAddress(): Observable<{ ip: string }> {
    return this.http.get<{ ip: string }>("https://api.ipify.org?format=json");
  }

  private modifyBusinessId(url: string): string {
    const urlObj = new URL(url, this.hostUrl);
    return urlObj.toString();
  }
}
